import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import LoginHeroSlideshow from './LoginHeroSlideshow';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = await login(username, password);
      if (userData?.is_app_only) {
        toast.success('App-Konto erkannt — Weiterleitung zur App');
        window.location.href = '/m';
        return;
      }
      if (userData?.must_change_password) {
        toast.info('Bitte ändern Sie Ihr Initial-Passwort');
        navigate('/passwort-aendern');
        return;
      }
      toast.success('Erfolgreich angemeldet');
      const redirect = searchParams.get('redirect');
      navigate(redirect && redirect.startsWith('/') ? redirect : '/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img 
              src="/images/logo.jpg" 
              alt="Musik Akademie Meyersick gGmbH" 
              className="mx-auto h-20 w-auto object-contain mb-6"
              data-testid="login-logo"
            />
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-2" data-testid="login-title">
              Verwaltungssystem
            </h1>
            <p className="text-slate-600">Melden Sie sich an, um fortzufahren</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8" data-testid="login-form">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="username">Benutzername, Anzeigename oder E-Mail</Label>
                <Input
                  id="username"
                  data-testid="username-input"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                      placeholder="Ihr Benutzername, Anzeigename oder E-Mail"
                  required
                  className="h-10"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Ihr Passwort"
                  required
                  className="h-10"
                />
                <div className="text-right">
                  <a href="/passwort-vergessen" data-testid="forgot-password-link"
                     className="text-xs text-slate-500 hover:text-slate-800 underline">
                    Passwort vergessen?
                  </a>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="login-button"
                className="w-full bg-brand-red text-white hover:bg-brand-red/90 h-10"
                disabled={loading}
              >
                {loading ? 'Anmeldung läuft...' : 'Anmelden'}
              </Button>
            </form>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <a href="/m" data-testid="login-mobile-app-link"
               className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white py-3 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:border-slate-400">
              <svg className="w-4 h-4 text-rose-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="7" y="2" width="10" height="20" rx="2"/><line x1="12" y1="18" x2="12" y2="18.01"/></svg>
              Mobile App
            </a>
            <a href="/portal/login" data-testid="login-portal-link"
               className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white py-3 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:border-slate-400">
              <svg className="w-4 h-4 text-brand-red" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
              Eltern-Portal (App)
            </a>
          </div>
        </div>
      </div>

      <LoginHeroSlideshow />
    </div>
  );
}
